import PopupBasic from 'src/game/components/popups/PopupBasic';
import View from '@play-co/timestep-core/lib/ui/View';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import ScrollView from '@play-co/timestep-core/lib/ui/ScrollView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import sounds from 'src/lib/sounds';
import { assertNever } from 'src/replicant/utils';
import { track } from 'src/lib/analytics/utils';

import { AssetGroup } from '@play-co/timestep-core';
import { isInLineMiniApp } from '@play-co/gcinstant';

type Mods = 'pp' | 'tos' | 'tosJPKk' | 'tokusyoho' | 'kessai';

export default class PopupTermsPolicyDetails extends PopupBasic {
  private container: ScrollView;
  private text: LangBitmapFontTextView;
  private cta: ButtonScaleViewWithText;

  constructor(private creationOpts: { superview: View; close: () => void }) {
    super({
      ...creationOpts,
      height: 800,
      width: 560,
      skipMessage: true,
      darkerBg: true,
      tracking: { feature: 'terms', $subFeature: 'popup-terms-policy-details' },
    });

    this.root.updateOpts({ zIndex: 10005 });
    this.bg.updateOpts({ opacity: 0.95 });
    this.buttonClose.hide();

    // create scrollable container
    this.container = new ScrollView({
      superview: this.box,
      x: 20,
      width: 520,
      height: 594,
      y: 80,
      scrollX: false,
      scrollY: true,
    });

    this.text = new LangBitmapFontTextView({
      superview: this.container,
      width: 520,
      align: 'left',
      y: 8,
      //verticalAlign: 'center',
      size: 24,
      color: '#9A7171',
      wordWrap: true,
      forceWrap: true,
      // Use slightly smaller font for the long text
      font: bitmapFonts('Body'),
      isRichText: true,
    });

    // OKAY button
    this.cta = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 40,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.box.style.height - 70,
      width: 240,
      height: 78,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.playSound('ok');
        creationOpts.close();
      },
      tracking: {
        feature: 'terms',
        $subFeature: 'popup-terms-policy-details-ok',
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  public init({ mode }: { mode: Mods }) {
    super.init({
      title: this.getTitle(mode),
    });

    track('ConfirmToS_Details_view', { mode });

    this.text.localeText = () => this.getContent(mode);

    // set scroll bounds
    this.container.updateOpts({
      scrollBounds: {
        minY: 0,
        maxY: this.text.getTextHeight() + 50,
      },
    });

    this.cta.localeText = () => this.getCta(mode);
  }

  private getTitle(mode: Mods) {
    switch (mode) {
      case 'pp':
        return i18n('platform.line.terms.pp.title');
      case 'tos':
        return i18n('platform.line.terms.tos.title');
      case 'tosJPKk':
        return i18n('platform.line.terms.tosJpKk.title');
      case 'tokusyoho':
        return i18n('platform.line.terms.tokusyoho.title');
      case 'kessai':
        return i18n('platform.line.terms.kessai.title');
      default:
        assertNever(mode);
    }
  }

  private getContent(mode: Mods) {
    switch (mode) {
      case 'pp':
        return i18n('platform.line.terms.pp.content');
      case 'tos':
        return i18n('platform.line.terms.tos.content');
      case 'tosJPKk':
        return i18n('platform.line.terms.tosJpKk.content');
      case 'tokusyoho':
        return i18n('platform.line.terms.tokusyoho.content');
      case 'kessai':
        return isInLineMiniApp()
          ? i18n('platform.line.terms.kessaiMiniApp.content')
          : i18n('platform.line.terms.kessai.content');
      default:
        assertNever(mode);
    }
  }

  private getCta(mode: Mods) {
    switch (mode) {
      case 'pp':
        return i18n('platform.line.terms.pp.cta');
      case 'tos':
        return i18n('platform.line.terms.tos.cta');
      case 'tosJPKk':
        return i18n('platform.line.terms.tosJpKk.cta');
      case 'tokusyoho':
        return i18n('platform.line.terms.tokusyoho.cta');
      case 'kessai':
        return i18n('platform.line.terms.kessai.cta');
      default:
        assertNever(mode);
    }
  }
}
